import { Injectable } from '@angular/core';
import { AuthService } from '@services/auth/auth.service';
import { ConfigAppService } from './config-app.service';
import { PopUpModalCustomService } from './popup-modal-custom.service';

/**
 * Texto del cartel cuando el negocio prende `sistemaTramitesSuspendido`.
 * Se puede pisar desde `configuracion_apps.mensajeSistemaSuspendido` sin
 * deployar el front.
 */
const MENSAJE_SUSPENDIDO_DEFAULT =
    'Les informamos que, debido a un problema técnico ajeno a nosotros, la realización de trámites online se encuentra suspendida temporalmente.\n\n✔ <strong>Importante:</strong> Su credencial digital y las demás funciones continúan disponibles con total normalidad.';

/**
 * Cartel temporal de sistema suspendido (ej. caída del hosting).
 *
 * El flag vive en `configuracion_apps.sistemaTramitesSuspendido`, la colección
 * que ya sirve `GET /app-config/findAll`. Se muestra en dos momentos:
 *
 * - el login del web (no hay usuario todavía), para el afiliado que entra;
 * - el arranque de la app, para quien ya tenía la sesión abierta. Ahí se limita
 *   a los perfiles afiliados con la MISMA regla que el redirect de
 *   `AuthService.redirigirSegunPerfil()`: un administrativo, sede o admin nunca
 *   lo ve.
 *
 * `yaMostrado` evita que las dos rutas lo abran dos veces seguidas en la misma
 * carga de la app.
 */
@Injectable({ providedIn: 'root' })
export class AvisoSuspensionService {
    private yaMostrado = false;

    constructor(
        private configAppService: ConfigAppService,
        private modalCustom: PopUpModalCustomService,
        private authService: AuthService,
    ) {}

    /**
     * @param soloAfiliados `true` en el arranque de la app: sólo perfiles
     * afiliados (los mismos que redirige a `/`). `false` en el login, donde
     * todavía no hay usuario.
     */
    async mostrarSiCorresponde(opciones: {
        soloAfiliados: boolean;
    }): Promise<void> {
        try {
            if (this.yaMostrado) {
                return;
            }

            if (!this.configAppService.configApp) {
                await this.configAppService.cargarConfiguracion();
            }

            const config = this.configAppService.configApp;
            if (!config || !config.sistemaTramitesSuspendido) {
                return;
            }

            if (opciones.soloAfiliados) {
                if (!this.authService.hasToken()) {
                    return;
                }

                const usuario = await this.authService.getUserLogon();
                // Misma regla que el redirect: si es administrativo, no lo ve.
                if (this.authService.esAdministrativo(usuario)) {
                    return;
                }
            }

            this.yaMostrado = true;

            // Si el campo está vacío (undefined, '', o sólo espacios) se usa el
            // texto por defecto: el negocio puede dejarlo en blanco y igual ve
            // el cartel completo.
            const mensaje =
                (config.mensajeSistemaSuspendido || '').trim() ||
                MENSAJE_SUSPENDIDO_DEFAULT;

            this.modalCustom.abrirPopup({
                tipo: 'warning',
                titulo: 'Aviso importante a nuestros afiliados',
                mensaje,
                textoBoton: 'Entendido',
                mostrarBoton: true,
                // Sticky: ningún otro popup lo reemplaza mientras está abierto.
                sticky: true,
                ancho: '480px',
            });
        } catch (error) {
            console.error(
                'No se pudo verificar el estado del sistema:',
                error,
            );
        }
    }
}
